import React, { ReactNode } from 'react';

import cx from 'classnames';

import { GlossIcon } from 'glossDS/Icons/GlossIcon/GlossIcon';

import { useFocusTrap } from 'hooks/useFocusTrap/useFocusTrap';

import Close from '../Icons/CloseV2.svg';

import sNew from './Modal.module.css';
import sLegacy from './ModalLegacy.module.css';

type ModalProps = {
  title?: string;
  children: ReactNode;
  className?: string;
  titleClassName?: string;
  showClose?: boolean;
  id?: string | undefined;
  ariaLabel?: string;
  onClose?: () => void;
  bottomDrawer?: boolean;
  contentClassName?: string;
  showNewCloseIcon?: boolean;
  'aria-labelledby'?: React.ComponentProps<'div'>['aria-labelledby'];
  'aria-label'?: React.ComponentProps<'div'>['aria-label'];
};

const Modal = ({
  className,
  titleClassName,
  children,
  showClose = true,
  title,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onClose,
  bottomDrawer,
  contentClassName,
  showNewCloseIcon = false,
  ...other
}: ModalProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const s = id ? sNew : sLegacy;
  const popover = id ? 'auto' : undefined;
  const popovertarget = id || undefined;
  const popovertargetaction = id ? 'hide' : undefined;
  const { ref } = useFocusTrap(isPopoverOpen);
  const ariaLabelledByValue =
    ariaLabelledBy || title ? 'modal-title' : undefined;

  React.useEffect(() => {
    // commenting as newState is not YET in the Event type properties
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const beforeToggleHandler = ({ newState }: Event) => {
      setIsPopoverOpen(newState === 'open');
    };

    if (ref.current?.hasAttribute('popover')) {
      const popover = ref.current;

      popover?.addEventListener('beforetoggle', beforeToggleHandler);

      return () => {
        popover?.removeEventListener('beforetoggle', beforeToggleHandler);
      };
    }
  }, [ref]);

  const handleOnClose = () => {
    if (onClose) {
      onClose();
    } else if (id) {
      // commenting as hidePopover is not YET in the Event type properties
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref.current?.hidePopover();
    }
  };

  return (
    <>
      {!id && <div className={s.backdrop} />}
      <div
        className={cx({
          [s.bottomDrawer]: bottomDrawer,
          [s.root]: !bottomDrawer,
        })}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledByValue}
        aria-label={ariaLabel}
        id={id}
        // eslint-disable-next-line react/no-unknown-property
        popover={popover}
        ref={ref}
      >
        <div className={cx(s.modal, className)} {...other}>
          {title && (
            <h2 id="modal-title" className={cx(s.title, titleClassName)}>
              {title}
            </h2>
          )}
          <div className={cx(s.text, contentClassName)}>{children}</div>
          {showClose && (
            <button
              aria-label="Close modal"
              type="button"
              className={s.closeButton}
              // eslint-disable-next-line react/no-unknown-property
              popovertarget={popovertarget}
              // eslint-disable-next-line react/no-unknown-property
              popovertargetaction={popovertargetaction}
              onClick={handleOnClose}
            >
              {showNewCloseIcon ? (
                <GlossIcon icon="close-outline" size="md" />
              ) : (
                <Close />
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Modal;
